import {Col, Empty, Grid, Row, Tag, Typography} from 'antd'
import {Link, useLoaderData} from 'react-router'
import {sageCreamPalette} from '../components/AppShell/Theme'
import type {BlogCategory, BlogCategoryDetail, BlogListing, BlogListingPost} from './blog.server'

const {Title, Paragraph, Text} = Typography
const {useBreakpoint} = Grid

export type BlogListingLoaderData = BlogListing & {category: BlogCategoryDetail | null}

function sizedImageUrl(url: string, width: number): string {
  try {
    const image = new URL(url)
    image.searchParams.set('w', String(width))
    image.searchParams.set('auto', 'format')
    return image.toString()
  } catch {
    return url
  }
}

function formattedDate(value: string | null): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : new Intl.DateTimeFormat('en', {dateStyle: 'long', timeZone: 'UTC'}).format(date)
}

const postHref = (post: BlogListingPost) => `/blog/${encodeURIComponent(post.slug.current)}`

function PostMeta({post}: {post: BlogListingPost}) {
  const date = formattedDate(post.publishedAt)
  return <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px'}}>
    {post.category?.title && <Tag color="green" style={{margin: 0}}>{post.category.title}</Tag>}
    {date && <time dateTime={post.publishedAt!}><Text type="secondary">{date}</Text></time>}
  </div>
}

function Cover({post, width, height}: {post: BlogListingPost; width: number; height: number | string}) {
  if (!post.coverUrl) return <div aria-hidden style={{width: '100%', height, background: sageCreamPalette.sageSoft}} />
  return <img
    src={sizedImageUrl(post.coverUrl, width)}
    srcSet={[width / 2, width, width * 2].map((w) => `${sizedImageUrl(post.coverUrl!, Math.round(w))} ${Math.round(w)}w`).join(', ')}
    sizes={`(max-width: 768px) 100vw, ${width}px`}
    alt={post.coverAlt || ''}
    style={{display: 'block', width: '100%', height, objectFit: 'cover'}}
  />
}

const cardStyle = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  color: 'inherit',
  textDecoration: 'none',
  background: sageCreamPalette.cardBg,
  border: `1px solid ${sageCreamPalette.border}`,
  borderRadius: 18,
  boxShadow: '0 10px 28px rgba(49, 55, 43, 0.07)',
} as const

function Hero({post, isDesktop}: {post: BlogListingPost; isDesktop: boolean}) {
  return <Link to={postHref(post)} aria-label={`Featured: ${post.title}`}
    style={{...cardStyle, flexDirection: isDesktop ? 'row' : 'column', height: 'auto', marginBottom: 32}}>
    <div style={{flex: isDesktop ? '0 0 55%' : undefined}}>
      <Cover post={post} width={900} height={isDesktop ? '100%' : 220} />
    </div>
    <div style={{padding: isDesktop ? 40 : 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12}}>
      <Text strong style={{color: sageCreamPalette.sage, letterSpacing: 1, textTransform: 'uppercase', fontSize: 12}}>Featured</Text>
      <Title level={2} className="display-heading" style={{margin: 0}}>{post.title}</Title>
      {post.excerpt && <Paragraph style={{margin: 0, fontSize: 16}}>{post.excerpt}</Paragraph>}
      <PostMeta post={post} />
    </div>
  </Link>
}

export function PostCard({post}: {post: BlogListingPost}) {
  return <Link to={postHref(post)} style={cardStyle}>
    <Cover post={post} width={480} height={180} />
    <div style={{padding: 20, display: 'flex', flexDirection: 'column', gap: 10, flex: 1}}>
      <Title level={4} className="display-heading" style={{margin: 0, color: sageCreamPalette.sageDark}}>{post.title}</Title>
      {post.excerpt && <Paragraph ellipsis={{rows: 3}} style={{margin: 0}}>{post.excerpt}</Paragraph>}
      <div style={{marginTop: 'auto'}}><PostMeta post={post} /></div>
    </div>
  </Link>
}

function CategoryNav({categories, activeSlug}: {categories: BlogCategory[]; activeSlug?: string}) {
  if (categories.length === 0) return null
  const chip = (active: boolean) => ({
    padding: '6px 16px',
    borderRadius: 999,
    textDecoration: 'none',
    border: `1px solid ${active ? sageCreamPalette.sage : sageCreamPalette.borderStrong}`,
    background: active ? sageCreamPalette.sage : 'transparent',
    color: active ? '#fff' : sageCreamPalette.text,
  })
  return <nav aria-label="Blog categories" style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32}}>
    <Link to="/blog" style={chip(!activeSlug)} aria-current={!activeSlug ? 'page' : undefined}>All</Link>
    {categories.map((category) => <Link key={category._id} to={`/blog/category/${encodeURIComponent(category.slug)}`}
      style={chip(category.slug === activeSlug)} aria-current={category.slug === activeSlug ? 'page' : undefined}>
      {category.title}
    </Link>)}
  </nav>
}

function Pagination({basePath, page, totalPages}: {basePath: string; page: number; totalPages: number}) {
  if (totalPages <= 1) return null
  const href = (target: number) => (target === 1 ? basePath : `${basePath}?page=${target}`)
  const linkStyle = {color: sageCreamPalette.sageDark, fontWeight: 600}
  return <nav aria-label="Pagination" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24, marginTop: 40}}>
    {page > 1 ? <Link to={href(page - 1)} rel="prev" style={linkStyle}>← Newer</Link> : <span />}
    <Text type="secondary">Page {page} of {totalPages}</Text>
    {page < totalPages ? <Link to={href(page + 1)} rel="next" style={linkStyle}>Older →</Link> : <span />}
  </nav>
}

export default function BlogListingPage() {
  const {hero, posts, categories, category, page, totalPages} = useLoaderData<BlogListingLoaderData>()
  const screens = useBreakpoint()
  const basePath = category ? `/blog/category/${encodeURIComponent(category.slug)}` : '/blog'
  const isEmpty = !hero && posts.length === 0

  return <main style={{maxWidth: 1180, margin: '0 auto', padding: screens.lg ? '48px 32px' : '28px 18px'}}>
    <header style={{marginBottom: 28}}>
      <Title level={1} className="display-heading" style={{margin: 0}}>{category ? category.title : 'Blog'}</Title>
      {category?.description && <Paragraph style={{marginTop: 8, fontSize: 16}}>{category.description}</Paragraph>}
    </header>
    <CategoryNav categories={categories} activeSlug={category?.slug} />
    {isEmpty ? <Empty description={category ? 'No posts in this category yet.' : 'No posts yet. Check back soon.'} style={{padding: '48px 0'}}>
      {category && <Link to="/blog">Browse all posts</Link>}
    </Empty> : <>
      {hero && page === 1 && <Hero post={hero} isDesktop={!!screens.lg} />}
      <Row gutter={[24, 24]}>
        {posts.map((post) =>
          <Col key={post._id} xs={24} md={12} lg={8}><PostCard post={post} /></Col>)}
      </Row>
      <Pagination basePath={basePath} page={page} totalPages={totalPages} />
    </>}
  </main>
}
